import { useEffect, useRef, useState } from "react";
import { Bell } from "@phosphor-icons/react";
import { formatET } from "../../lib/dateUtils";
import { api } from "../../lib/api";

export function RepNotificationBell() {
  const [callbacks, setCallbacks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchCallbacks = async () => {
    try {
      const { data } = await api.get("/rep/callbacks");
      setCallbacks(data.filter((c) => c.status === "pending"));
    } catch {
      // ignore
    }
  };

  const fetchMessages = async () => {
    try {
      const { data } = await api.get("/rep/notifications", { params: { limit: 10 } });
      setMessages(data.notifications);
      setUnreadMessages(data.unread_count);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchCallbacks();
    fetchMessages();
    const interval = setInterval(() => {
      fetchCallbacks();
      fetchMessages();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markMessageRead = async (id) => {
    try {
      await api.patch(`/rep/notifications/${id}/read`);
      fetchMessages();
    } catch {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/rep/notifications/read-all");
      fetchMessages();
    } catch {
      // ignore
    }
  };

  const totalBadgeCount = callbacks.length + unreadMessages;

  return (
    <div className="relative" ref={ref}>
      <button data-testid="rep-notification-bell-button" onClick={() => setOpen(!open)} title="Opens messages from your manager and your pending callback reminders." className="relative p-2 text-igc-cream hover:text-igc-goldText transition-colors">
        <Bell size={22} weight={totalBadgeCount > 0 ? "fill" : "duotone"} />
        {totalBadgeCount > 0 && (
          <span data-testid="rep-notification-unread-count" className="absolute -top-1 -right-1 bg-igc-gold text-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">
            {totalBadgeCount > 9 ? "9+" : totalBadgeCount}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="rep-notification-dropdown" className="absolute right-0 mt-3 w-80 bg-igc-surface border border-igc-gold/20 shadow-2xl z-50 max-h-[28rem] overflow-y-auto">
          <div className="p-4 border-b border-igc-gold/10 flex items-center justify-between">
            <p className="font-serif text-igc-cream">Messages</p>
            {unreadMessages > 0 && (
              <button data-testid="rep-notification-mark-all-read" onClick={markAllRead} title="Marks every message here as read." className="text-igc-goldText text-xs hover:underline">
                Mark all read
              </button>
            )}
          </div>
          {messages.length === 0 && <p className="p-4 text-igc-muted text-sm text-center" data-testid="rep-notification-messages-empty">No messages from your manager yet.</p>}
          {messages.map((m) => (
            <button
              key={m.id}
              data-testid={`rep-notification-message-${m.id}`}
              onClick={() => !m.read && markMessageRead(m.id)}
              title="Click to mark this message as read."
              className={`w-full text-left p-4 border-b border-igc-gold/5 border-l-2 ${m.read ? "border-l-transparent" : "border-l-igc-gold"}`}
            >
              <p className="text-igc-goldText text-xs mb-1">{m.from_name ? `From ${m.from_name}` : "From your manager"}</p>
              <p className="text-igc-cream text-sm">{m.message}</p>
              <p className="text-igc-muted text-xs mt-1">{formatET(m.timestamp, "MMM d, yyyy h:mm a")} ET</p>
            </button>
          ))}
          <div className="p-4 border-b border-t border-igc-gold/10">
            <p className="font-serif text-igc-cream">Pending Callbacks</p>
          </div>
          {callbacks.length === 0 && <p className="p-6 text-igc-muted text-sm text-center" data-testid="rep-notification-dropdown-empty">No pending callbacks.</p>}
          {callbacks.map((c) => (
            <div key={c.id} data-testid={`rep-notification-item-${c.id}`} className="p-4 border-b border-igc-gold/5">
              <p className="text-igc-cream text-sm">{c.lead_name}</p>
              <p className="text-igc-muted text-xs mt-1">{formatET(c.scheduled_at, "MMM d, yyyy h:mm a")} ET</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
